/**
 * Cliente de /api/meet/:token en psiplatform-be (proxy hacia psi-video: videollamada embebida
 * de la psicóloga). Solo lo usa la pantalla `/meet/:token`. Mismo auth y base URL que `beHttp`;
 * no usa sus toasts: la pantalla muestra sus propios estados.
 */
import { PSI_PLATFORM_BE_URL } from "./config";
import { getValidToken, hasValidSession, redirectToLogin } from "./auth";

export interface MeetSession {
  sessionId: string;
  /** Proceso (api-core) de la sesión: con él se llama a los endpoints de paciente que ya existen. */
  processId: string;
  start: string;
  durationMin: number;
  status: string;
}

export interface MeetView {
  provider: "psi-video" | "meet";
  meetLink: string | null;
  serverTime: string;
  suggestedSessionId: string | null;
  sessions: MeetSession[];
}

export interface MeetEntry {
  provider: "psi-video" | "meet";
  sessionId: string | null;
  url: string | null;
}

/** `invalid`: el link no existe o no es de esta psicóloga; `unavailable`: cualquier otro problema */
export class MeetError extends Error {
  constructor(public readonly kind: "invalid" | "unavailable") {
    super(kind);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = hasValidSession() ? getValidToken() : null;
  if (!token) {
    redirectToLogin();
    throw new MeetError("unavailable");
  }
  let resp: Response;
  try {
    resp = await fetch(`${PSI_PLATFORM_BE_URL}/api/meet/${path}`, {
      ...init,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    throw new MeetError("unavailable");
  }
  if (resp.status === 400) throw new MeetError("invalid");
  if (resp.status === 404) {
    // Solo es "link inválido" si lo dice el backend; un 404 pelado es que la ruta no existe (backend sin actualizar)
    const body = (await resp.json().catch(() => null)) as {
      code?: string;
    } | null;
    throw new MeetError(
      body?.code === "invalid_link" ? "invalid" : "unavailable",
    );
  }
  if (!resp.ok) throw new MeetError("unavailable");
  return (await resp.json()) as T;
}

export const fetchMeetView = (linkToken: string) =>
  call<MeetView>(encodeURIComponent(linkToken));

export const enterMeet = (linkToken: string, sessionId?: string) =>
  call<MeetEntry>(`${encodeURIComponent(linkToken)}/enter`, {
    method: "POST",
    body: JSON.stringify(sessionId ? { sessionId } : {}),
  });

export interface MeetPresencePerson {
  everJoined: boolean;
  connectedNow: boolean;
  firstJoinedAt: string | null;
  lastLeftAt: string | null;
}

/** Presencia de la sesión según psi-video: quién está conectado y las cuentas de la política de tolerancia. */
export interface MeetPresence {
  sessionId: string;
  roomExists: boolean;
  serverTime: string;
  start: string | null;
  minutesSinceStart: number | null;
  patient: MeetPresencePerson;
  professional: MeetPresencePerson;
  absence: {
    show: boolean;
    minutesElapsed: number;
    minutesToTolerance: number;
    reportRequired: boolean;
  };
  exit: { toleranceOver: boolean; belowMinimum: boolean };
  rules: { toleranceMin: number; reportMin: number; minSessionMin: number };
}

export const fetchMeetPresence = (linkToken: string, sessionId: string) =>
  call<MeetPresence>(
    `${encodeURIComponent(linkToken)}/presence?sessionId=${encodeURIComponent(sessionId)}`,
  );
